import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";

/* ═════════════════════════════════════════════════════════════════════
   DISPATCHER MESSAGE BOARD
   ─────────────────────────────────────────────────────────────────────
   Sidebar shows logged-in dispatchers + in-service ambulance units (each
   with a green LED dot to indicate online). Dispatcher ctrl-clicks one
   or more recipients, types a message in the bottom composer, and hits
   Send — the backend fans out one message per recipient and pushes a
   `message_received` WS event to every connected client.

   Center pane:
     - When 0 or 1 recipient is selected → shows the running thread for
       that selection (sender/receiver bubbles, oldest at top).
     - When >1 recipient is selected → composer becomes a multi-cast
       broadcast tool; thread view collapses to a "Multi-cast preview".

   Replying:
     - Dispatchers: can reply to anyone.
     - Supervisor units (unit_type == "SUPV"): backend allows; UI exposes
       a Reply button on each received message in the MDT MSG LIST.
     - Regular ambulance crews: backend rejects POST /messages; the MDT
       UI does not show a reply button.
   ═════════════════════════════════════════════════════════════════════ */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function militaryHMS(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch {
    return "";
  }
}

export default function MessageBoard({ user, units, lastMessageEvent }) {
  const me = useMemo(
    () => ({
      type: "dispatcher",
      id: user?.user_id || user?.id || "",
      // Per operator: the displayed sender label on the recipient side must
      // be the dispatch ID (e.g. "D902"), NOT the human name. We pass
      // dispatcher_userid as sender_name so the MDT renders "FROM: D902".
      name: user?.dispatcher_userid || user?.username || "",
      humanName: user?.username || "",
    }),
    [user],
  );

  const [roster, setRoster] = useState({ units: [], dispatchers: [] });
  const [selected, setSelected] = useState([]); // array of { type, id, name }
  const [allMessages, setAllMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const messagesEndRef = useRef(null);

  // Pull online roster every 15s + on every WS unit/user change.
  const fetchRoster = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/messages/online-roster`);
      setRoster(r.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchRoster();
    // Refresh the online roster every 8 seconds (was 15) so newly-logged-in
    // dispatchers and units show up promptly. Combined with the 30-second
    // heartbeat interval and the 5-minute backend "active" window, the
    // online dot reflects reality within ~8s.
    const t = setInterval(fetchRoster, 8000);
    return () => clearInterval(t);
  }, [fetchRoster]);

  // Refresh roster whenever a unit toggles in/out of service.
  useEffect(() => { fetchRoster(); }, [units, fetchRoster]);

  // Load my message history.
  const fetchMessages = useCallback(async () => {
    if (!me.id) return;
    try {
      const r = await axios.get(`${API}/messages`, { params: { user_type: me.type, user_id: me.id } });
      setAllMessages(r.data || []);
    } catch {}
  }, [me]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  // Live updates from parent WS handler.
  useEffect(() => {
    if (!lastMessageEvent) return;
    if (lastMessageEvent.type === "message_received" || lastMessageEvent.type === "message_read") {
      fetchMessages();
    }
  }, [lastMessageEvent, fetchMessages]);

  // Auto-scroll to bottom when thread updates.
  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [allMessages, selected]);

  // Active thread when a single recipient is selected → all messages
  // exchanged with that party (in either direction). Otherwise empty
  // (multi-cast composer mode).
  const thread = useMemo(() => {
    if (selected.length !== 1) return [];
    const r = selected[0];
    return allMessages
      .filter((m) =>
        (m.sender_type === r.type && m.sender_id === r.id) ||
        (m.recipient_type === r.type && m.recipient_id === r.id)
      )
      .sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1));
  }, [allMessages, selected]);

  // Mark received messages as read when they show up in the open thread.
  useEffect(() => {
    if (selected.length !== 1) return;
    const me_id = me.id;
    const me_type = me.type;
    const unread = thread.filter((m) => !m.read_at && m.recipient_type === me_type && m.recipient_id === me_id);
    unread.forEach((m) => {
      axios.put(`${API}/messages/${m.id}/read`).catch(() => {});
    });
  }, [thread, me.id, me.type, selected]);

  const toggleRecipient = (rec, e) => {
    setError("");
    const isMulti = e?.ctrlKey || e?.metaKey;
    setSelected((prev) => {
      const exists = prev.find((p) => p.type === rec.type && p.id === rec.id);
      if (!isMulti) return exists ? [] : [rec];
      return exists ? prev.filter((p) => !(p.type === rec.type && p.id === rec.id)) : [...prev, rec];
    });
  };

  const handleSend = async () => {
    if (!body.trim() || selected.length === 0 || sending) return;
    setError("");
    setSending(true);
    try {
      const r = await axios.post(`${API}/messages`, {
        sender_type: me.type,
        sender_id: me.id,
        sender_name: me.name,
        recipients: selected,
        body: body.trim(),
      });
      if (r.data?.error) {
        setError(r.data.error);
      } else {
        setBody("");
        fetchMessages();
      }
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to send message");
    }
    setSending(false);
  };

  const handleDelete = async (msg) => {
    try {
      await axios.delete(`${API}/messages/${msg.id}`, {
        params: { requester_type: me.type, requester_id: me.id },
      });
      fetchMessages();
    } catch {}
  };

  const handleReply = (msg) => {
    // Quote the original sender as the new recipient.
    setSelected([{ type: msg.sender_type, id: msg.sender_id, name: msg.sender_name }]);
    setBody("");
    // Focus the composer.
    const ta = document.querySelector("[data-testid='msgboard-composer']");
    if (ta) ta.focus();
  };

  // Build sidebar entries — every dispatcher + every logged-in unit
  // (regardless of supervisor flag). Show by UNIT ID / DISPATCH ID per
  // operator spec — never the human name. Self is included with a "(You)"
  // suffix and is non-selectable so dispatchers can confirm their own
  // online status without accidentally messaging themselves.
  const sidebarItems = useMemo(() => {
    const dispatchers = (roster.dispatchers || []).map((d) => ({
      type: "dispatcher",
      id: d.user_id,
      // Display the dispatcher userid (e.g. "D902"); fallback to
      // username if older heartbeat row doesn't carry it yet.
      name: d.dispatcher_userid || d.username,
      sub: d.username || "Dispatcher",
      online: true,
      isSelf: d.user_id === me.id,
    }));
    const inService = (roster.units || []).map((u) => ({
      type: "unit",
      id: u.id,
      name: u.name,  // e.g. "CBVAC-6"
      sub: `${u.unit_type || u.unit_level || "AMB"} • ${u.agency || ""}`,
      online: true,
      isSupervisor: ((u.unit_type || "") + "").toUpperCase() === "SUPV" || ((u.unit_type || "") + "").toUpperCase() === "SUPERVISOR",
    }));
    return [...dispatchers, ...inService];
  }, [roster, me.id]);

  return (
    <div data-testid="msgboard-root" style={{ display: "flex", height: "calc(100vh - 56px)", background: "#0B1119", color: "#D8E0EC" }}>
      {/* ───────────── Sidebar ───────────── */}
      <div style={{ width: 280, borderRight: "1px solid #1F2A3C", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "10px 14px", borderBottom: "1px solid #1F2A3C", background: "#101826" }}>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.5, color: "#7AB8E8" }}>RECIPIENTS</div>
          <div style={{ fontSize: 10, color: "#6F7E92", marginTop: 2 }}>Click to select • Ctrl+Click for multiple</div>
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {sidebarItems.length === 0 && (
            <div style={{ padding: 14, color: "#6F7E92", fontSize: 12 }}>No one online.</div>
          )}
          {sidebarItems.map((item) => {
            const isSelected = !!selected.find((s) => s.type === item.type && s.id === item.id);
            const isSelf = item.isSelf;
            return (
              <div
                key={`${item.type}-${item.id}`}
                data-testid={`msgboard-recipient-${item.type}-${item.id}`}
                onClick={(e) => { if (!isSelf) toggleRecipient(item, e); }}
                style={{
                  padding: "8px 14px",
                  borderBottom: "1px solid #16202E",
                  cursor: isSelf ? "default" : "pointer",
                  background: isSelected ? "#1A3A5A" : "transparent",
                  opacity: isSelf ? 0.7 : 1,
                  display: "flex", alignItems: "center", gap: 10,
                }}
              >
                <span
                  data-testid={`msgboard-online-${item.type}-${item.id}`}
                  style={{
                    width: 9, height: 9, borderRadius: "50%",
                    background: item.online ? "#3DDB6E" : "#444",
                    boxShadow: item.online ? "0 0 6px #3DDB6E" : "none",
                    flex: "0 0 9px",
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: isSelected ? "#FFF" : "#D8E0EC", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {item.name}
                    {isSelf && <span style={{ marginLeft: 6, fontSize: 10, color: "#88BBEE" }}>(You)</span>}
                    {item.isSupervisor && <span style={{ marginLeft: 6, fontSize: 10, color: "#FFCC44" }}>SUPV</span>}
                  </div>
                  <div style={{ fontSize: 10, color: "#6F7E92" }}>{item.sub}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ───────────── Center: Thread + Composer ───────────── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "10px 18px", borderBottom: "1px solid #1F2A3C", background: "#101826", fontSize: 13, color: "#7AB8E8", fontWeight: 700, letterSpacing: 0.5 }}>
          {selected.length === 0 && "MESSAGE BOARD — select a recipient"}
          {selected.length === 1 && `THREAD — ${selected[0].name}`}
          {selected.length > 1 && `BROADCAST — ${selected.length} recipients selected`}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px", background: "#080D14" }}>
          {selected.length === 1 && thread.map((m) => {
            const fromMe = m.sender_type === me.type && m.sender_id === me.id;
            return (
              <div key={m.id} data-testid={`msgboard-msg-${m.id}`}
                style={{ display: "flex", justifyContent: fromMe ? "flex-end" : "flex-start", marginBottom: 10 }}>
                <div style={{ maxWidth: "70%" }}>
                  <div style={{ fontSize: 10, color: "#6F7E92", marginBottom: 3, textAlign: fromMe ? "right" : "left" }}>
                    {fromMe ? "ME" : m.sender_name} • {militaryHMS(m.timestamp)}
                  </div>
                  <div style={{
                    background: fromMe ? "#1A4A7A" : "#1A2638",
                    border: `1px solid ${fromMe ? "#2A6AAA" : "#2A3A52"}`,
                    color: "#EAF0FA", borderRadius: 6, padding: "8px 12px", fontSize: 13, lineHeight: 1.4, whiteSpace: "pre-wrap",
                  }}>
                    {m.body}
                  </div>
                  <div style={{ marginTop: 4, display: "flex", gap: 10, justifyContent: fromMe ? "flex-end" : "flex-start" }}>
                    {!fromMe && (
                      <button data-testid={`msgboard-reply-${m.id}`} onClick={() => handleReply(m)}
                        style={{ fontSize: 10, color: "#7AB8E8", background: "transparent", border: "1px solid #2A3A52", padding: "1px 8px", borderRadius: 3, cursor: "pointer" }}>
                        Reply
                      </button>
                    )}
                    <button data-testid={`msgboard-delete-${m.id}`} onClick={() => handleDelete(m)}
                      style={{ fontSize: 10, color: "#A8526A", background: "transparent", border: "1px solid #4A2632", padding: "1px 8px", borderRadius: 3, cursor: "pointer" }}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {selected.length === 0 && (
            <div style={{ color: "#6F7E92", fontSize: 13, textAlign: "center", marginTop: 40 }}>
              Pick a dispatcher or unit from the left panel to begin a conversation. Ctrl+Click to broadcast to multiple recipients at once.
            </div>
          )}
          {selected.length > 1 && (
            <div style={{ color: "#FFC78A", fontSize: 13, textAlign: "center", marginTop: 40 }}>
              Broadcast mode — your next message will be delivered to all {selected.length} selected recipients.
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Composer */}
        <div style={{ padding: 12, borderTop: "1px solid #1F2A3C", background: "#0E1622", display: "flex", flexDirection: "column", gap: 6 }}>
          {error && <div style={{ color: "#FF6868", fontSize: 12 }}>{error}</div>}
          <div style={{ display: "flex", gap: 10 }}>
            <textarea
              data-testid="msgboard-composer"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={selected.length === 0 ? "Select a recipient first…" : "Type your message and press Send (Shift+Enter for new line)"}
              disabled={selected.length === 0 || sending}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              style={{
                flex: 1, minHeight: 56, maxHeight: 120, resize: "vertical",
                background: "#1A2638", border: "1px solid #2A3A52", color: "#EAF0FA",
                fontFamily: "inherit", fontSize: 13, padding: "8px 12px", borderRadius: 4,
              }}
            />
            <button
              data-testid="msgboard-send"
              onClick={handleSend}
              disabled={selected.length === 0 || sending || !body.trim()}
              style={{
                width: 110, fontSize: 13, fontWeight: 700, color: "#FFF",
                background: selected.length === 0 || !body.trim()
                  ? "#2A3A52"
                  : "linear-gradient(180deg, #2D8FE0 0%, #1A6BBC 100%)",
                border: "1px solid #1A4A7A", borderRadius: 4,
                cursor: selected.length === 0 || !body.trim() ? "not-allowed" : "pointer",
              }}
            >
              {sending ? "Sending..." : "SEND"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
